import { Project, ProjectWithStatuses } from "@/utils/types";
import { Canvas } from "../AI/PRD/constants";
import { useDispatch, useSelector } from "react-redux";
import {
  setCreateType,
  setDeleteProjectCandidateId,
  setEditProjectCandidateId,
  setShowAddEditProjectModal,
  setShowDeleteProjectModal,
} from "@/store/projectsSlice";
import { useState, useEffect, useRef, useLayoutEffect } from "react";
import { X } from "lucide-react";
import { Chat } from "../AI/chat/Chat";
import { AI_AGENT } from "@/store/zustand/aiActionStore";
import React from "react";
import CanvasKanban from "./CanvasKanban";
import ProjectSettings from "../settings";
import ProjectConfiguration from "../ProjectConfiguration";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "../ui/resizable";
import _ from "lodash";
import { getAxiosErorrMessage, getUnexpiredToken } from "@/utils/utils";
import { toast } from "sonner";
import axios from "axios";
import { useProjectWithUsers } from "@/hooks/useProjectWithUsers";
import { useNavigate, useSearchParams } from "react-router";
import { ProjectPageHeader } from "./ProjectPageHeader";
import { ProjectPageNav } from "@/store/zustand/layoutStore";
import UnauthorizedModal from "../UnauthorizedModal";
import { setUnauthorized } from "@/store/codebaseSlice";
import { useUser } from "@clerk/clerk-react";
import { useSession } from "@clerk/clerk-react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { ProjectSidebar } from "./ProjectSidebar";
import { PanelRenderer } from "./PanelRenderer";
import ViewsPanel from "../views/ViewsPanel";
import { ContextBar } from "@/components/ui/context-bar";
import { useIssueSelectionStore } from "@/store/zustand/issueSelectionStore";
import {
  ShortcutProviderRoot,
  ShortcutHandler,
} from "../providers/ShortcutProvider";
import { useCommandPalette } from "@/hooks/useCommandPalette";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useFocusStore } from "@/store/zustand/focusStore";

import PRDModal from "../AI/PRD/PRDModal";

import { useDatabaseSyncWrapperStore } from "../contexts/databaseSyncStore";
import { usePMAgentStore } from "@/store/zustand/pmAgentStore";
import { useIssuesStore } from "@/store/zustand/issues-store";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import { useConversationSwitching } from "@/hooks/useConversationSwitching";
import { ProjectConfigTab } from "../ProjectConfiguration/ProjectConfigNav";
import { useViews } from "../views/hooks/useViews";
import { FilterState } from "./types";
import { useMemo, useCallback } from "react";
import {
  serializeFiltersToParams,
  deserializeFiltersFromParams,
  getDefaultFilters,
} from "@/utils/filterUrlUtils";

interface ProjectPageProps {}

const ProjectPage: React.FC<ProjectPageProps> = () => {
  const { user } = useUser();
  const { session } = useSession();

  // Issue selection state
  const selectedIssueIds = useIssueSelectionStore(
    (state) => state.selectedIssueIds,
  );
  const clearSelection = useIssueSelectionStore(
    (state) => state.clearSelection,
  );
  const hasSelection = selectedIssueIds.size > 0;

  const clearFocus = useFocusStore((state) => state.clearFocus);

  // PM Agent - get masterCanvasId setter from store
  const setMasterCanvasId = usePMAgentStore((state) => state.setMasterCanvasId);

  const { open } = useCommandPalette();

  const [searchParams, setSearchParams] = useSearchParams();

  const { getViews } = useViews();

  const selectedView = searchParams.get("view_id");
  const canvasIdFromUrl = searchParams.get("canvas_id");

  // --- Add settings tab state ---
  const [activeSettingsTab, setActiveSettingsTab] = useState<ProjectConfigTab>(
    ProjectConfigTab.STATUSES,
  );

  // --- AI Chat panel sizing ---
  const [aiChatPanelSize, setAiChatPanelSize] = useState(30); // percentage
  const [aiChatMinSize, setAiChatMinSize] = useState(15); // percentage
  const [aiChatMaxSize, setAiChatMaxSize] = useState(50); // percentage

  // Calculate min/max sizes based on screen width (similar to editor)
  useEffect(() => {
    const calculatePanelConstraints = () => {
      const containerWidth = window.innerWidth;
      // Min 320px, max 50% of screen
      const minSizePercent = Math.max(15, (320 / containerWidth) * 100);
      const maxSizePercent = 50;
      setAiChatMinSize(minSizePercent);
      setAiChatMaxSize(maxSizePercent);
    };

    calculatePanelConstraints();
    window.addEventListener("resize", calculatePanelConstraints);
    return () =>
      window.removeEventListener("resize", calculatePanelConstraints);
  }, []);

  const navigate = useNavigate();

  const baseProjects = useSelector(
    (state: any) => state.supabaseStore.projects,
  );

  const currentProject = useProjectWithUsers();

  // Get project issues to find master canvas
  const projectIssues = useIssuesStore(
    (state) => state.issuesByProject[currentProject?.id ?? ""],
  );

  // Find master canvas from project issues
  const masterCanvasId = useMemo(() => {
    if (!projectIssues) return null;
    const masterIssue = Object.values(projectIssues).find(
      (issue) => issue.canvas.is_master_canvas,
    );
    return masterIssue?.canvas.id ?? null;
  }, [projectIssues]);

  const dispatch = useDispatch<any>();

  const deleteProjectCandidateId = useSelector(
    (state: any) => state.projects.deleteProjectCandidateId,
  );
  const editProjectCandidateId = useSelector(
    (state: any) => state.projects.editProjectCandidateId,
  );
  const showDeleteProjectModal = useSelector(
    (state: any) => state.projects.showDeleteProjectModal,
  );
  const showAddEditProjectModal = useSelector(
    (state: any) => state.projects.showAddEditProjectModal,
  );

  const projectPageNav = useMemo(() => {
    const navParam = searchParams.get("nav");
    if (navParam === "AIChat") return ProjectPageNav.AIChat;
    if (navParam === "Views") return ProjectPageNav.Views;
    if (navParam === "Settings") return ProjectPageNav.Settings;
    if (navParam === "Info") return ProjectPageNav.Info;
    return null; // Default: kanban only, no sidebar
  }, [searchParams]);

  const setProjectPageNav = useCallback(
    (nav: ProjectPageNav | null) => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);

        if (nav === null) {
          newParams.delete("nav");
          newParams.delete("view_id");
          Array.from(newParams.keys()).forEach((key) => {
            if (key.startsWith("filter_")) {
              newParams.delete(key);
            }
          });
        } else {
          newParams.set("nav", nav);

          if (nav === ProjectPageNav.Views) {
            newParams.delete("viewType");
          } else if (
            nav === ProjectPageNav.Settings ||
            nav === ProjectPageNav.Info
          ) {
            Array.from(newParams.keys()).forEach((key) => {
              if (key !== "project_id" && key !== "nav") {
                newParams.delete(key);
              }
            });
          }
        }

        return newParams;
      });
    },
    [setSearchParams],
  );

  const filters: FilterState = useMemo(() => {
    const parsedFilters = deserializeFiltersFromParams(searchParams);
    if (!parsedFilters) return getDefaultFilters();

    return {
      ...getDefaultFilters(),
      ...parsedFilters,
    };
  }, [searchParams]);

  const updateFilters = useCallback(
    (newFilters: Partial<FilterState>) => {
      // Allow filter updates when in kanban mode (no sidebar) or AIChat mode
      if (projectPageNav !== null && projectPageNav !== ProjectPageNav.AIChat) {
        return;
      }

      setSearchParams((prev) => {
        const merged = { ...filters, ...newFilters };
        return serializeFiltersToParams(prev, merged);
      });
    },
    [filters, setSearchParams, projectPageNav],
  );

  const clearFilters = useCallback(() => {
    setSearchParams((prev) => {
      return serializeFiltersToParams(prev, getDefaultFilters());
    });
  }, [setSearchParams]);

  const deleteProjectCandidate = baseProjects?.[deleteProjectCandidateId];

  const closeDeleteProjectModal = () => {
    dispatch(setShowDeleteProjectModal(false));
    dispatch(setDeleteProjectCandidateId(null));
  };

  const unauthorized = useSelector((state: any) => state.codebase.unauthorized);

  const deleteProject = async (user) => {
    const supabaseJWT = await getUnexpiredToken(session);

    axios
      .post(
        `${
          import.meta.env.VITE_BACKEND_BASE_URL
        }/projects/${deleteProjectCandidateId}/deleteUser`,
        {},
        {
          headers: {
            Authorization: `Bearer ${supabaseJWT}`,
          },
        },
      )
      .then(() => {
        toast.success("Project deleted successfully");
        closeDeleteProjectModal();
        useDatabaseSyncWrapperStore.getState().triggerRefetchProjects();

        // Check if this was the last project and navigate to project creation if so
        // this is different from the first time no projects case where they go to project-create/workflow-select instead
        const remainingProjects = Object.keys(baseProjects || {}).filter(
          (id) => id !== deleteProjectCandidateId,
        );
        if (remainingProjects.length === 0) {
          navigate("/project-create/ai-new");
        }
      })
      .catch((err) => {
        toast.error(getAxiosErorrMessage(err));
      });
  };

  const prdModalEnabled = useFeatureFlagEnabled("prd-quick-view");

  const openCanvas = (canvas: Canvas) => {
    if (prdModalEnabled) {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("canvas_id", canvas.id);
        return newParams;
      });
    } else {
      navigate(`/canvases/${canvas.id}/editor`);
    }
  };

  const viewType = useMemo(() => {
    const urlViewType = searchParams.get("viewType");
    if (urlViewType === "grid" || urlViewType === "list") {
      return urlViewType;
    }
    return "grid";
  }, [searchParams]);

  const setViewType = useCallback(
    (newViewType: "grid" | "list") => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("viewType", newViewType);
        return newParams;
      });
    },
    [setSearchParams],
  );

  useEffect(() => {
    if (
      projectPageNav === ProjectPageNav.Views ||
      projectPageNav === ProjectPageNav.Settings
    ) {
      clearSelection();
      clearFocus();
    }
  }, [projectPageNav, clearSelection, clearFocus]);

  useEffect(() => {
    document.title = currentProject?.name
      ? `${currentProject?.name} - Tempo`
      : "Tempo";
  }, [currentProject?.name]);

  // Set masterCanvasId in store for PM Agent when AIChat sidebar is open
  const isAIChatOpen = projectPageNav === ProjectPageNav.AIChat;
  useEffect(() => {
    if (isAIChatOpen && masterCanvasId) {
      setMasterCanvasId(masterCanvasId);
    } else if (!isAIChatOpen) {
      setMasterCanvasId(null);
    }
  }, [isAIChatOpen, masterCanvasId, setMasterCanvasId]);

  // Initialize conversation after masterCanvasId is in store
  // This runs on the next render after the above effect sets the store
  const storeMasterCanvasId = usePMAgentStore((state) => state.masterCanvasId);
  const { switchToModelConversation } = useConversationSwitching();

  useEffect(() => {
    if (isAIChatOpen && storeMasterCanvasId) {
      // Initialize conversation - creates one if needed and selects it
      // (useCanvasId in the hook now returns storeMasterCanvasId)
      switchToModelConversation().then(() => {
        // Set status to succeeded so Chat component knows conversations are loaded
        useSupabaseStore.setState({ aiChatConversationsStatus: "succeeded" });
      });
    }
  }, [isAIChatOpen, storeMasterCanvasId, switchToModelConversation]);

  const handleViewSelect = (viewId: string) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);

      if (!prev.get("nav")) {
        newParams.set("nav", "Views");
      }

      if (viewId && viewId !== "create-new") {
        newParams.set("view_id", viewId);
      } else {
        newParams.delete("view_id");
      }

      return newParams;
    });
  };

  return (
    <ShortcutProviderRoot>
      <ShortcutHandler enabled={projectPageNav !== ProjectPageNav.Settings}>
        <div className="flex h-full w-full">
          <ProjectSidebar
            activeNav={projectPageNav}
            onNavChange={setProjectPageNav}
          />

          {/* Don't use PanelRenderer for AIChat - it's rendered as a ResizablePanel */}
          <PanelRenderer
            activeNav={
              projectPageNav === ProjectPageNav.AIChat ? null : projectPageNav
            }
            currentProject={currentProject}
            selectedView={selectedView}
            onViewSelect={handleViewSelect}
            activeSettingsTab={activeSettingsTab}
            onSettingsTabChange={setActiveSettingsTab}
          />
          <div className="flex flex-col flex-1 overflow-x-auto">
            {projectPageNav === ProjectPageNav.Views ? (
              // Views panel - full screen when a view is selected
              <div className="flex-1 overflow-auto">
                <ViewsPanel
                  project={currentProject}
                  selectedViewId={selectedView || ""}
                  onViewSelect={handleViewSelect}
                />
              </div>
            ) : projectPageNav === ProjectPageNav.Settings ? (
              // Settings panel - full screen when settings is selected
              <div className="flex-1 overflow-auto">
                {currentProject ? (
                  <ProjectConfiguration project={currentProject} />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-secondary-n-400">
                      Loading project settings...
                    </p>
                  </div>
                )}
              </div>
            ) : (
              // Main content area with header and resizable panels
              <>
                {/* --- New Circle UI style header bar --- */}
                <div className="w-full bg-background">
                  <ProjectPageHeader
                    project={currentProject}
                    setViewType={setViewType}
                    viewType={viewType}
                    filters={filters}
                    updateFilters={updateFilters}
                  />
                </div>

                <ResizablePanelGroup direction="horizontal">
                  {/* AI Chat Panel - resizable, on the left */}
                  {isAIChatOpen && (
                    <>
                      <ResizablePanel
                        order={1}
                        defaultSize={aiChatPanelSize}
                        minSize={aiChatMinSize}
                        maxSize={aiChatMaxSize}
                        onResize={(size) => setAiChatPanelSize(size)}
                      >
                        <div className="h-full border-r border-secondary-n-900">
                          <Chat agent={AI_AGENT.PM_AGENT} />
                        </div>
                      </ResizablePanel>
                      <ResizableHandle withHandle />
                    </>
                  )}

                  {showAddEditProjectModal && (
                    <>
                      <ResizablePanel
                        order={2}
                        defaultSize={30}
                        maxSize={60}
                        style={{ minWidth: "400px" }}
                      >
                        <div className="h-full flex flex-col border-r border-secondary-n-900">
                          <div className="flex items-center justify-between px-4 py-2.5 border-b border-secondary-n-900">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-medium text-secondary-n-50">
                                {editProjectCandidateId
                                  ? baseProjects?.[editProjectCandidateId]?.name
                                  : "New Project"}
                              </span>
                            </div>
                            <button
                              onClick={() => {
                                if (editProjectCandidateId) {
                                  useDatabaseSyncWrapperStore
                                    .getState()
                                    .triggerRefetchProjects();
                                }
                                dispatch(setShowAddEditProjectModal(false));
                                dispatch(setEditProjectCandidateId(null));
                                dispatch(setCreateType(null));
                                setProjectPageNav(null);
                              }}
                              className="p-1 hover:bg-secondary-n-900 rounded-md"
                            >
                              <X className="w-4 h-4 text-secondary-n-400" />
                            </button>
                          </div>
                          <div className="flex-1 overflow-hidden">
                            <ProjectSettings
                              editProjectId={editProjectCandidateId}
                              editProjectData={
                                editProjectCandidateId
                                  ? baseProjects?.[editProjectCandidateId]
                                  : null
                              }
                              scrollAreaHeight="h-[calc(100vh-200px)]"
                            />
                          </div>
                        </div>
                      </ResizablePanel>
                      <ResizableHandle withHandle />
                    </>
                  )}

                  <ResizablePanel
                    order={3}
                    defaultSize={
                      isAIChatOpen
                        ? showAddEditProjectModal
                          ? 70 - aiChatPanelSize
                          : 100 - aiChatPanelSize
                        : showAddEditProjectModal
                          ? 70
                          : 100
                    }
                    minSize={30}
                  >
                    <div className="flex flex-col flex-1 min-w-0">
                      <div className="flex-1">
                        <DndProvider backend={HTML5Backend}>
                          <CanvasKanban
                            onSelect={openCanvas}
                            project={
                              currentProject as ProjectWithStatuses | null
                            }
                            filters={filters}
                            viewType={viewType}
                            clearFilters={clearFilters}
                          />
                        </DndProvider>
                      </div>
                    </div>
                  </ResizablePanel>
                </ResizablePanelGroup>
              </>
            )}
          </div>

          <AlertDialog
            open={showDeleteProjectModal}
            onOpenChange={(open: boolean) => {
              if (!open) {
                closeDeleteProjectModal();
              }
            }}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="text-xs font-normal">
                  Delete Project
                </AlertDialogTitle>
                <AlertDialogDescription>
                  <div className="w-full flex flex-col">
                    <div className="pb-2 text-xs font-normal">
                      Are you sure you want to delete this project?
                    </div>
                    <div className="flex flex-row gap-1">
                      <div className="text-text-primary text-xs font-normal">
                        Project Name:
                      </div>
                      <div className="text-text-primary text-xs font-normal">
                        {deleteProjectCandidate?.name}
                      </div>
                    </div>
                    <div className="flex flex-row gap-1">
                      <div className="text-text-primary text-xs font-normal">
                        Repo URL:
                      </div>
                      <div className="text-text-primary text-xs font-normal">
                        {deleteProjectCandidate?.repoPath}
                      </div>
                    </div>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel
                  onClick={() => {
                    closeDeleteProjectModal();
                  }}
                >
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  variant="destructive"
                  onClick={() => {
                    setSearchParams((prev) => {
                      const newParams = {
                        ...prev,
                      };
                      delete newParams["project_id"];
                      return newParams;
                    });
                    deleteProject(user);
                  }}
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {unauthorized && (
            <UnauthorizedModal
              onClose={() => {
                dispatch(setUnauthorized(false));
                const fullProjectsList = Object.values(
                  baseProjects || {},
                ) as any[];
                const recentProject =
                  fullProjectsList.length > 0 ? fullProjectsList[0] : null;
                if (recentProject) {
                  setSearchParams((prev) => ({
                    ...prev,
                    project_id: recentProject.id,
                  }));
                } else {
                  navigate("/");
                }
              }}
            />
          )}

          {/* ContextBar for issue selection */}
          {hasSelection && (
            <ContextBar
              count={selectedIssueIds.size}
              onClose={clearSelection}
              onCommand={() => {
                open();
              }}
              buttons={[]}
            />
          )}
          {prdModalEnabled && canvasIdFromUrl && currentProject && (
            <PRDModal
              canvasId={canvasIdFromUrl}
              projectId={currentProject.id}
              isOpen={!!canvasIdFromUrl}
              closeModal={() => {
                setSearchParams((prev) => {
                  const newParams = new URLSearchParams(prev);
                  newParams.delete("canvas_id");
                  return newParams;
                });
              }}
            />
          )}
        </div>
      </ShortcutHandler>
    </ShortcutProviderRoot>
  );
};

export default ProjectPage;
